/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { authenticatedFetch } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { cn } from "../../../shared/components/ui/cn";
import { Input } from "../../../shared/components/ui/input";
import { useToast } from "../../../shared/hooks/use-toast";
import { useCascadeFadeIn } from "../../../shared/hooks/useCascadeFadeIn";
import { BasicInfoTable } from "./components/BasicInfoTable";
import { BulkActionsToolbar } from "./components/BulkActionsToolbar";
import { CastCrewTable } from "./components/CastCrewTable";
import { CollectionPickerModal } from "./components/CollectionPickerModal";
import { FilterBar } from "./components/FilterBar";
import { Pagination } from "./components/Pagination";
import { QuickStatsTable } from "./components/QuickStatsTable";
import { TitlesDataTable } from "./components/TitlesDataTable";
import { VideoAssetsTable } from "./components/VideoAssetsTable";
import { ViewModeSelector } from "./components/ViewModeSelector";
import { VisualAssetsTable } from "./components/VisualAssetsTable";
import { useBulkActions } from "./hooks/useBulkActions";
import { useBulkTitles, useTitleStats, useUpdateTitleField } from "./hooks/useBulkTitles";
import { useFilterState } from "./hooks/useFilterState";
import type { BulkEditTitle, CastMember, CrewMember, ViewMode } from "./types";

/**
 * Animated wrapper for table views with fade-in effect
 */
function AnimatedTableView({
  children,
  enabled = true,
}: {
  readonly children: React.ReactNode;
  readonly enabled?: boolean;
}) {
  const { ref, isVisible } = useCascadeFadeIn({
    index: 0,
    threshold: 0.1,
    staggerDelay: 0,
    enabled,
  });

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-500 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
      )}
    >
      {children}
    </div>
  );
}

/**
 * Bulk Edit Titles Page
 *
 * Provides a data table interface for editing multiple titles quickly.
 * Admins can edit key fields inline or navigate to full editor for complex changes.
 */
export function BulkEditTitles(): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [collectionModalTitleId, setCollectionModalTitleId] = useState<string | null>(null);

  // View mode state (persisted to localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem("bulkEditorViewMode");
    return (saved as ViewMode) || "basic";
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem("bulkEditorViewMode", mode);
  };

  // Fetch titles (without stats initially for faster load)
  const { titles, loading, error, refetch, updateTitleLocally, mergeStats, refreshSingleTitle } = useBulkTitles();
  const { updateField } = useUpdateTitleField();
  const { fetchStats } = useTitleStats();

  // Filter state with URL sync
  const { filterState, updateFilters, clearFilters, hasActiveFilters } = useFilterState();

  // Fetch stats when switching to stats view
  useEffect(() => {
    if (viewMode === "stats" && titles.length > 0 && !titles[0]?.stats) {
      void fetchStats().then(mergeStats);
    }
  }, [viewMode, titles, fetchStats, mergeStats]);

  // Pagination state from URL
  const currentPage = Number.parseInt(searchParams.get("page") || "1", 10);
  const pageSize = Number.parseInt(searchParams.get("pageSize") || "25", 10);

  // Handle adding titles to a collection
  const handleAddToCollection = async (collectionKey: string) => {
    // Determine which titles to add: single title or bulk selection
    const titleIds = collectionModalTitleId ? [collectionModalTitleId] : Array.from(selectedIds);

    if (titleIds.length === 0 || !accessToken) return;

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

      const res = await authenticatedFetch<{ added: number; skipped: number }>(
        `${baseUrl}/v1/admin/titles/bulk-add-to-collection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ titleIds, collectionKey }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      if (collectionModalTitleId) {
        // Single title: refresh stats to update collection count
        const statsMap = await fetchStats();
        mergeStats(statsMap);
      } else {
        // Bulk: clear selection
        setSelectedIds(new Set());
      }

      const skippedText = res.skipped > 0 ? ` (${res.skipped} already in collection)` : "";
      toast({
        description: `Added ${res.added} title${res.added > 1 ? "s" : ""} to collection${skippedText}`,
      });
    } catch (err) {
      console.error("Failed to add titles to collection:", err);
      toast({
        description: err instanceof Error ? err.message : "Failed to add titles to collection",
        variant: "destructive",
      });
    }
  };

  // Handle removing a title from a collection
  const handleRemoveFromCollection = async (collectionKey: string) => {
    if (!collectionModalTitleId || !accessToken) return;

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

      await authenticatedFetch(
        `${baseUrl}/v1/admin/titles/bulk-remove-from-collection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ titleIds: [collectionModalTitleId], collectionKey }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      // Refresh stats to update collection count
      const statsMap = await fetchStats();
      mergeStats(statsMap);

      toast({
        description: "Removed title from collection",
      });
    } catch (err) {
      console.error("Failed to remove title from collection:", err);
      toast({
        description: err instanceof Error ? err.message : "Failed to remove title from collection",
        variant: "destructive",
      });
    }
  };

  // Pagination state from URL    // Bulk action handlers
  const { handleBulkPublish, handleBulkUnpublish, handleBulkDelete, handleBulkExportCsv } = useBulkActions({
    titles,
    selectedIds,
    setSelectedIds,
    updateField,
    updateTitleLocally,
    refetch,
  });

  // Apply filters and search to titles
  const filteredTitles = useMemo(() => {
    return titles.filter((title) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          title.name.toLowerCase().includes(query) ||
          title.directorName?.toLowerCase().includes(query) ||
          title.year?.toString().includes(query) ||
          title.genres.some((g) => g.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filterState.status === "published" && !title.published) return false;
      if (filterState.status === "draft" && title.published) return false;

      // Genre filter
      if (filterState.genres.length > 0) {
        const hasMatchingGenre = filterState.genres.some((genre) => title.genres.includes(genre));
        if (!hasMatchingGenre) return false;
      }

      // Year range filter
      if (title.year) {
        const [minYear, maxYear] = filterState.yearRange;
        if (title.year < minYear || title.year > maxYear) return false;
      }

      return true;
    });
  }, [titles, searchQuery, filterState]);

  // Pagination
  const totalPages = Math.ceil(filteredTitles.length / pageSize);
  const paginatedTitles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTitles.slice(startIndex, endIndex);
  }, [filteredTitles, currentPage, pageSize]);

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
    setSelectedIds(new Set()); // Clear selection on page change
  };

  const handlePageSizeChange = (size: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageSize", size.toString());
    newParams.set("page", "1"); // Reset to first page
    setSearchParams(newParams);
    setSelectedIds(new Set());
  };

  const handleFieldUpdate = async (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | CastMember[] | CrewMember[] | null,
  ) => {
    // Optimistically update the UI immediately
    const updates: Partial<BulkEditTitle> = { [field]: value };
    updateTitleLocally(titleId, updates);

    // Show success toast (no title, just description)
    const fieldLabels: Record<string, string> = {
      name: "Title",
      year: "Year",
      directorName: "Director",
      published: "Status",
      runtimeMinutes: "Runtime",
      country: "Country",
      languages: "Languages",
      rating: "Rating",
      castData: "Cast",
      crewData: "Crew",
    };

    toast({
      description: `${fieldLabels[field] || "Field"} updated successfully`,
      variant: "default",
    });

    // Save to backend in the background
    try {
      await updateField(titleId, field, value);
    } catch (err) {
      console.error("Failed to update field:", err);

      // Show error toast (no title, just description)
      toast({
        description: err instanceof Error ? err.message : "Failed to update field",
        variant: "destructive",
      });

      // Refetch to restore correct state from server
      await refetch();
    }
  };

  // Delete handler for single title
  const handleDelete = async (titleId: string, titleName: string) => {
    if (!confirm(`Are you sure you want to delete "${titleName}"? This action cannot be undone.`)) {
      return;
    }

    if (!accessToken) {
      toast({ description: "Not authenticated", variant: "destructive" });
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
      await authenticatedFetch(
        `${baseUrl}/v1/admin/titles/bulk-delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ titleIds: [titleId] }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      await refetch();
      toast({ description: `Deleted "${titleName}"` });
    } catch (err) {
      console.error("Delete failed:", err);
      toast({
        description: err instanceof Error ? err.message : "Failed to delete title",
        variant: "destructive",
      });
    }
  };

  // Duplicate handler for single title
  const handleDuplicate = (titleId: string) => {
    // Navigate to the editor with duplicate param
    navigate(`/admin/titles/${titleId}/edit?duplicate=true`);
  };

  // Manage collections handler
  const handleManageCollections = (titleId: string, _titleName: string) => {
    setCollectionModalTitleId(titleId);
    setShowCollectionPicker(true);
  };

  // Selection handlers
  const handleSelectAll = (selected: boolean) => {
    setSelectedIds(selected ? new Set(paginatedTitles.map((t) => t.id)) : new Set());
  };

  const handleSelectOne = (titleId: string, selected: boolean) => {
    const newSelection = new Set(selectedIds);
    if (selected) {
      newSelection.add(titleId);
    } else {
      newSelection.delete(titleId);
    }
    setSelectedIds(newSelection);
  };

  if (loading) {
    return (
      <div className="mx-auto py-12 text-center">
        <LoadingSpinner className="mx-auto" />
        <p className="text-neutral-400 mt-4">Loading titles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto py-12">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto py-8 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Bulk Edit Titles</h1>
            <p className="text-sm text-neutral-400 pb-6">
              Edit title metadata inline, filter by status/genre/year, and take bulk actions.
            </p>
          </div>
          <Button variant="ghost-sm" onClick={() => navigate("/admin/titles")}>
            ← Back to Titles Library
          </Button>
        </div>
      </div>

      {/* View Mode Selector */}
      <ViewModeSelector value={viewMode} onChange={handleViewModeChange} />

      {/* Search */}
      <div className="flex gap-2 items-center">
        <Input
          type="text"
          placeholder="Search by title, director, year, or genre..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="px-4 py-2 text-neutral-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        )}
        <span className="text-sm text-neutral-500">
          {filteredTitles.length} of {titles.length} titles
        </span>
      </div>

      {/* Filter Bar */}
      <FilterBar
        titles={titles}
        filterState={filterState}
        onFilterChange={updateFilters}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Data Table */}
      {paginatedTitles.length > 0 ? (
        <>
          {viewMode === "basic" && (
            <AnimatedTableView>
              <BasicInfoTable
                titles={paginatedTitles}
                selectedIds={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectOne={handleSelectOne}
                onFieldUpdate={handleFieldUpdate}
                onDelete={(id, name) => void handleDelete(id, name)}
                onDuplicate={handleDuplicate}
              />
            </AnimatedTableView>
          )}
          {viewMode === "visual" && (
            <AnimatedTableView>
              <VisualAssetsTable
                titles={paginatedTitles}
                selected={selectedIds}
                onSelect={handleSelectOne}
                onSelectAll={handleSelectAll}
                onFieldUpdate={handleFieldUpdate}
                onDelete={(id, name) => void handleDelete(id, name)}
                onDuplicate={handleDuplicate}
                onRefresh={refreshSingleTitle}
              />
            </AnimatedTableView>
          )}
          {viewMode === "cast" && (
            <AnimatedTableView>
              <CastCrewTable
                titles={paginatedTitles}
                selectedIds={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectOne={handleSelectOne}
                onFieldUpdate={handleFieldUpdate}
                onDelete={(id, name) => void handleDelete(id, name)}
                onDuplicate={handleDuplicate}
              />
            </AnimatedTableView>
          )}
          {viewMode === "video" && (
            <AnimatedTableView>
              <VideoAssetsTable
                titles={paginatedTitles}
                selectedIds={selectedIds}
                onSelect={handleSelectOne}
                onSelectAll={handleSelectAll}
                onDelete={(id, name) => void handleDelete(id, name)}
                onDuplicate={handleDuplicate}
                onRefresh={refreshSingleTitle}
              />
            </AnimatedTableView>
          )}
          {viewMode === "stats" && (
            <AnimatedTableView>
              <QuickStatsTable
                titles={paginatedTitles}
                selectedIds={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectOne={handleSelectOne}
                onDelete={(id, name) => void handleDelete(id, name)}
                onDuplicate={handleDuplicate}
                onManageCollections={handleManageCollections}
              />
            </AnimatedTableView>
          )}
          {viewMode !== "basic" &&
            viewMode !== "visual" &&
            viewMode !== "cast" &&
            viewMode !== "video" &&
            viewMode !== "stats" && (
              <AnimatedTableView>
                <TitlesDataTable
                  titles={paginatedTitles}
                  selectedIds={selectedIds}
                  onSelectAll={handleSelectAll}
                  onSelectOne={handleSelectOne}
                  onFieldUpdate={handleFieldUpdate}
                  onDelete={(id, name) => void handleDelete(id, name)}
                  onDuplicate={handleDuplicate}
                />
              </AnimatedTableView>
            )}
        </>
      ) : (
        <div className="text-center py-12 bg-neutral-900/30 border border-neutral-800 rounded-lg">
          <p className="text-neutral-400">No titles found matching your filters.</p>
          <button
            type="button"
            onClick={() => {
              setSearchQuery("");
              clearFilters();
            }}
            className="mt-2 text-lumi-accent hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Pagination */}
      {filteredTitles.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredTitles.length}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedIds.size}
          onClearSelection={() => setSelectedIds(new Set())}
          onPublish={handleBulkPublish}
          onUnpublish={handleBulkUnpublish}
          onDelete={handleBulkDelete}
          onAddToCollection={() => setShowCollectionPicker(true)}
          onExportCsv={handleBulkExportCsv}
        />
      )}

      {/* Collection Picker Modal */}
      <CollectionPickerModal
        open={showCollectionPicker}
        onClose={() => {
          setShowCollectionPicker(false);
          setCollectionModalTitleId(null);
        }}
        onSelectCollection={(key) => void handleAddToCollection(key)}
        selectedCount={collectionModalTitleId ? 1 : selectedIds.size}
        titleCollections={
          collectionModalTitleId ? titles.find((t) => t.id === collectionModalTitleId)?.stats?.collections : undefined
        }
        onRemoveFromCollection={collectionModalTitleId ? (key) => void handleRemoveFromCollection(key) : undefined}
      />
    </div>
  );
}
